/**
 * Date/time formatter with user-timezone awareness — Sprint 22 W2.
 *
 * Берёт unix timestamp + конвертирует через Intl.DateTimeFormat в указанной
 * timezone (или таймзоне текущего user'а из window.__crmUser__).
 *
 * Использование:
 *   import { formatDateTime, formatDate } from '/public/js/utils/datetime-format.js';
 *   formatDateTime(1700000000);                      → "14.11.2023, 22:13" (Europe/Moscow)
 *   formatDateTime(1700000000, { tz: 'UTC' });       → "14.11.2023, 22:13" (UTC)
 *   formatDate(1700000000);                          → "14.11.2023"
 *   formatRelative(unixSec);                         → "5 мин назад" / "через 2 ч"
 *
 * Если timezone не задано — fallback на user.timezone → 'Europe/Moscow'.
 *
 * @module utils/datetime-format
 */

const DEFAULT_LOCALE = 'ru-RU';
const FALLBACK_TZ = 'Europe/Moscow';

function getUserTimezone() {
    const user = window.__crmUser__;
    return (user && user.timezone) || FALLBACK_TZ;
}

function getLocale() {
    const user = window.__crmUser__;
    const locale = user?.locale || user?.preferred_language || 'ru';
    if (locale === 'en') return 'en-US';
    if (locale === 'es') return 'es-ES';
    return DEFAULT_LOCALE;
}

/**
 * Format unix timestamp (seconds OR ms) as "DD.MM.YYYY, HH:mm".
 * @param {number} ts unix timestamp в секундах ИЛИ миллисекундах (heuristic)
 * @param {object} [opts]
 * @param {string} [opts.tz]      override timezone (по умолч. — user.timezone)
 * @param {boolean}[opts.seconds] показывать секунды
 * @param {string} [opts.locale]  override locale (по умолч. — user.locale)
 * @returns {string}
 */
export function formatDateTime(ts, opts = {}) {
    if (ts == null || ts === '') return '—';
    const ms = ts > 1e12 ? ts : ts * 1000;  // >1e12 = ms, иначе seconds
    try {
        return new Intl.DateTimeFormat(opts.locale || getLocale(), {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
            second: opts.seconds ? '2-digit' : undefined,
            timeZone: opts.tz || getUserTimezone()
        }).format(new Date(ms));
    } catch (_) {
        return new Date(ms).toLocaleString();
    }
}

/**
 * Date-only: "DD.MM.YYYY".
 */
export function formatDate(ts, opts = {}) {
    if (ts == null || ts === '') return '—';
    const ms = ts > 1e12 ? ts : ts * 1000;
    try {
        return new Intl.DateTimeFormat(opts.locale || getLocale(), {
            year: 'numeric', month: '2-digit', day: '2-digit',
            timeZone: opts.tz || getUserTimezone()
        }).format(new Date(ms));
    } catch (_) {
        return new Date(ms).toLocaleDateString();
    }
}

/**
 * Relative time: "5 мин назад" / "через 2 ч" / "вчера".
 */
export function formatRelative(ts) {
    if (ts == null) return '—';
    const ms = ts > 1e12 ? ts : ts * 1000;
    const diffSec = (ms - Date.now()) / 1000;
    const abs = Math.abs(diffSec);
    const past = diffSec < 0;

    try {
        const rtf = new Intl.RelativeTimeFormat(getLocale(), { numeric: 'auto' });
        if (abs < 60) return rtf.format(past ? -Math.floor(abs) : Math.floor(abs), 'second');
        if (abs < 3600) return rtf.format(past ? -Math.floor(abs / 60) : Math.floor(abs / 60), 'minute');
        if (abs < 86400) return rtf.format(past ? -Math.floor(abs / 3600) : Math.floor(abs / 3600), 'hour');
        if (abs < 2592000) return rtf.format(past ? -Math.floor(abs / 86400) : Math.floor(abs / 86400), 'day');
        return formatDate(ts);
    } catch (_) {
        return formatDate(ts);
    }
}

/**
 * Convert ISO/Date input → unix seconds для backend save.
 * Учитывает что input "2026-05-11T14:30" — local time в user's timezone.
 */
export function inputToUnix(localISOInput, tz) {
    if (!localISOInput) return null;
    // localISOInput от <input type="datetime-local"> в UTC interpretation.
    // Если user в другой timezone — нужно correctly convert. Простой подход:
    // считаем что input уже в user's local time, далее Date конструктор
    // интерпретирует как browser local. Если browser TZ != user TZ — sketchy.
    // В обычной workflow совпадает.
    const ms = Date.parse(localISOInput);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

// Expose на window для legacy кода (не-ESM caller'ов):
if (typeof window !== 'undefined') {
    window.__dtFormat__ = { formatDateTime, formatDate, formatRelative, inputToUnix };
}
